import React from 'react'
import {
  FaFileImport,
  FaFileInvoice,
  FaFolderOpen,
  FaGavel,
  FaHourglassHalf,
  FaLandmark,
  FaMagic,
  FaUserPlus,
  FaUserTie
} from 'react-icons/fa'
import {
  FaBoxArchive,
  FaBuildingColumns,
  FaCalendarDay,
  FaCalendarDays,
  FaClock,
  FaFileCircleExclamation,
  FaFileLines,
  FaPenRuler,
  FaRotate,
  FaTriangleExclamation
} from 'react-icons/fa6'
// Direct file import: this registry is evaluated inside the @components
// barrel's own import graph, so going through the barrel would be circular.
import { LucaIcon } from '@components/LucaIcon'
import { NocfoTheme } from '@styles/styled'
import nocfoTheme from '@styles/theme/nocfo'

export type ThemeColorName = keyof NocfoTheme['colors']

export type ColumnKey =
  | 'responsible'
  | 'automation'
  | 'deadline'
  | 'time_saved'
  | 'sales'
  | 'purchase'
  | 'sales_unpaid'
  | 'sales_overdue'
  | 'purchase_overdue'
  | 'debt'
  | 'drafts'
  | 'docs_month'
  | 'docs_year'
  | 'bank_count'
  | 'last_sync'
  | 'customer_bank'
  | 'luca'

export type ColumnSection = 'recommended' | 'invoicing' | 'efficiency' | 'banking' | 'luca'

export interface ColumnDef {
  key: ColumnKey
  fr: string
  minPx: number
  // Backend ordering key; columns without one are not sortable.
  sortKey?: string
  section: ColumnSection
  agencyOnly?: boolean
  // Listed in the column configurator with a coming-soon badge, but never
  // rendered in the table and not toggleable.
  comingSoon?: boolean
  icon: React.ReactNode
  // Theme color name the panel icon tile derives its colors from.
  iconColor: ThemeColorName
  iconMixPct: number
  iconDarken?: boolean
}

const SELECT_PX = 34
const BUSINESS_PX = 250
const CHEVRON_PX = 30
// Grid column gap; also the gap inside PinnedCell so the pinned block and
// the grid stay aligned. Feeds the min-width math below AND the CSS gap in
// BusinessTable.styled.tsx — one source, cannot drift.
export const CELL_GAP_PX = 16

export const COLUMN_ORDER: ColumnKey[] = [
  'responsible',
  'automation',
  'deadline',
  'drafts',
  'docs_month',
  'docs_year',
  'sales',
  'purchase',
  'sales_unpaid',
  'sales_overdue',
  'purchase_overdue',
  'debt',
  'time_saved',
  'bank_count',
  'last_sync',
  'customer_bank',
  'luca'
]

export const COLUMNS: Record<ColumnKey, ColumnDef> = {
  responsible: {
    key: 'responsible',
    fr: '1.5fr',
    minPx: 190,
    section: 'recommended',
    agencyOnly: true,
    comingSoon: true,
    icon: <FaUserTie />,
    iconColor: 'primary',
    iconMixPct: 12
  },
  automation: {
    key: 'automation',
    fr: '0.85fr',
    minPx: 110,
    sortKey: 'automation',
    section: 'recommended',
    // The magic wand marks automation rules everywhere in the app.
    icon: <FaMagic />,
    iconColor: 'success',
    iconMixPct: 16
  },
  deadline: {
    key: 'deadline',
    fr: '1.5fr',
    minPx: 200,
    sortKey: 'deadline',
    section: 'recommended',
    icon: <FaCalendarDays />,
    iconColor: 'warning',
    iconMixPct: 18
  },
  time_saved: {
    key: 'time_saved',
    fr: '0.9fr',
    minPx: 120,
    sortKey: 'time_saved',
    section: 'efficiency',
    icon: <FaClock />,
    iconColor: 'success',
    iconMixPct: 12,
    iconDarken: true
  },
  sales: {
    key: 'sales',
    fr: '0.9fr',
    minPx: 122,
    sortKey: 'sales',
    section: 'invoicing',
    icon: <FaFileInvoice />,
    iconColor: 'primary',
    iconMixPct: 12
  },
  purchase: {
    key: 'purchase',
    fr: '0.9fr',
    minPx: 130,
    sortKey: 'purchase',
    section: 'invoicing',
    icon: <FaFileImport />,
    iconColor: 'dashboardExtra2',
    iconMixPct: 14
  },
  sales_unpaid: {
    key: 'sales_unpaid',
    fr: '1fr',
    minPx: 138,
    sortKey: 'sales_unpaid',
    section: 'invoicing',
    icon: <FaHourglassHalf />,
    iconColor: 'warning',
    iconMixPct: 18
  },
  sales_overdue: {
    key: 'sales_overdue',
    fr: '1fr',
    minPx: 138,
    sortKey: 'sales_overdue',
    section: 'invoicing',
    icon: <FaTriangleExclamation />,
    iconColor: 'warning',
    iconMixPct: 12,
    iconDarken: true
  },
  purchase_overdue: {
    key: 'purchase_overdue',
    fr: '1fr',
    minPx: 150,
    sortKey: 'purchase_overdue',
    section: 'invoicing',
    icon: <FaFileCircleExclamation />,
    iconColor: 'warning',
    iconMixPct: 12,
    iconDarken: true
  },
  debt: {
    key: 'debt',
    fr: '1fr',
    minPx: 138,
    sortKey: 'debt',
    section: 'invoicing',
    icon: <FaGavel />,
    iconColor: 'warning',
    iconMixPct: 12,
    iconDarken: true
  },
  drafts: {
    key: 'drafts',
    fr: '0.7fr',
    minPx: 96,
    sortKey: 'drafts',
    section: 'recommended',
    icon: <FaPenRuler />,
    iconColor: 'iconGrayDark',
    iconMixPct: 12
  },
  docs_month: {
    key: 'docs_month',
    fr: '0.85fr',
    minPx: 116,
    sortKey: 'docs_month',
    section: 'recommended',
    icon: <FaFileLines />,
    iconColor: 'primary',
    iconMixPct: 12
  },
  docs_year: {
    key: 'docs_year',
    fr: '0.85fr',
    minPx: 116,
    sortKey: 'docs_year',
    section: 'recommended',
    icon: <FaFolderOpen />,
    iconColor: 'dashboardExtra2',
    iconMixPct: 14
  },
  bank_count: {
    key: 'bank_count',
    fr: '0.8fr',
    minPx: 112,
    sortKey: 'bank_count',
    section: 'banking',
    icon: <FaBuildingColumns />,
    iconColor: 'dashboardExtra2',
    iconMixPct: 14
  },
  last_sync: {
    key: 'last_sync',
    fr: '1fr',
    minPx: 132,
    sortKey: 'last_sync',
    section: 'banking',
    icon: <FaRotate />,
    iconColor: 'primary',
    iconMixPct: 12
  },
  customer_bank: {
    key: 'customer_bank',
    fr: '1.2fr',
    minPx: 168,
    section: 'banking',
    icon: <FaLandmark />,
    iconColor: 'menuBg',
    iconMixPct: 10
  },
  luca: {
    key: 'luca',
    fr: '0.85fr',
    minPx: 122,
    sortKey: 'luca',
    section: 'luca',
    icon: <LucaIcon />,
    iconColor: 'primary',
    iconMixPct: 12
  }
}

export const DEFAULT_COLUMNS: ColumnKey[] = ['automation', 'deadline', 'drafts']

// Columns the backend computes for every row regardless of ?columns=;
// everything else is only annotated on request. Mirrors the backend's
// _NON_OPTIONAL_COLUMNS — sections are purely visual grouping.
const ALWAYS_COMPUTED: ColumnKey[] = ['responsible', 'automation', 'deadline', 'time_saved']

// Optional columns the backend only computes on demand (?columns=).
export const OPTIONAL_COLUMNS: ColumnKey[] = COLUMN_ORDER.filter(
  key => !ALWAYS_COMPUTED.includes(key)
)

export const COLUMN_SECTIONS: { section: ColumnSection; keys: ColumnKey[] }[] = [
  {
    section: 'recommended',
    keys: ['responsible', 'automation', 'deadline', 'drafts', 'docs_month', 'docs_year']
  },
  {
    section: 'invoicing',
    keys: ['sales', 'purchase', 'sales_unpaid', 'sales_overdue', 'purchase_overdue', 'debt']
  },
  { section: 'efficiency', keys: ['time_saved'] },
  { section: 'banking', keys: ['bank_count', 'last_sync', 'customer_bank'] },
  { section: 'luca', keys: ['luca'] }
]

export interface FilterDef {
  key: 'vat_soon' | 'low_automation' | 'overdue' | 'archived'
  icon: React.ReactNode
  iconColor: ThemeColorName
  iconDarken?: boolean
  // Only offered to (and honored for) agency users.
  agencyOnly?: boolean
}

export const FILTER_DEFS: FilterDef[] = [
  { key: 'vat_soon', icon: <FaCalendarDay />, iconColor: 'error' },
  { key: 'low_automation', icon: <FaMagic />, iconColor: 'primary' },
  { key: 'overdue', icon: <FaTriangleExclamation />, iconColor: 'warning', iconDarken: true },
  { key: 'archived', icon: <FaBoxArchive />, iconColor: 'iconGrayDark', agencyOnly: true }
]

// Filters read the same data as these columns, so they follow the same
// backend permissions: no permitted column, no filter. Filters without an
// entry (archived) are permission-free.
export const FILTER_COLUMN_DEPENDENCIES: Partial<Record<FilterDef['key'], ColumnKey>> = {
  vat_soon: 'deadline',
  low_automation: 'automation',
  overdue: 'sales_overdue'
}

export const COMING_SOON_BULK_ACTIONS = [{ key: 'assign_users', icon: <FaUserPlus /> }]

// `available` is the permission-filtered option set from the backend
// constants API; null means it has not loaded (no filtering yet). The
// backend enforces regardless — this only keeps dead columns out of the UI.
export const visibleColumns = (
  enabled: ColumnKey[],
  isAgencyUser: boolean,
  available: Set<string> | null = null
): ColumnKey[] =>
  COLUMN_ORDER.filter(
    key =>
      enabled.includes(key) &&
      !COLUMNS[key].comingSoon &&
      (!COLUMNS[key].agencyOnly || isAgencyUser) &&
      (!available || available.has(key))
  )

// The rows' horizontal gutter is theme.spacing.lg (rem); the min-width math
// below needs it in px. All brand themes share nocfo's spacing scale.
const ROW_PAD_PX = nocfoTheme.spacing.lg * 16

export const gridTemplate = (
  columns: ColumnKey[],
  isAgencyUser: boolean
): { template: string; minWidth: number } => {
  // The first grid column is the sticky block: the row's left padding, the
  // agency select checkbox and the business cell live inside it so the whole
  // block can pin during horizontal scroll.
  const selectWidth = isAgencyUser ? SELECT_PX + CELL_GAP_PX : 0
  const pinnedMin = ROW_PAD_PX + selectWidth + 220

  const parts: string[] = [`minmax(${pinnedMin}px, 2.2fr)`]
  let minWidth = ROW_PAD_PX + selectWidth + BUSINESS_PX + CHEVRON_PX
  columns.forEach(key => {
    parts.push(COLUMNS[key].fr)
    minWidth += COLUMNS[key].minPx
  })
  parts.push(`${CHEVRON_PX}px`)
  minWidth += CELL_GAP_PX * (parts.length - 1) + ROW_PAD_PX

  return { template: parts.join(' '), minWidth }
}
